import React, { useState, useEffect, useRef } from 'react';
import { useStore, getUserById, getChatById, getChats } from '../../store';
import { Avatar } from '../ui/Avatar';
import { PaymentTracker } from '../payments/PaymentTracker';
import { sanitize } from '../../lib/security';
import { t } from '../../lib/i18n';
import toast from 'react-hot-toast';
import { db } from '../../lib/db';

export const ChatPanel: React.FC = () => {
  const {
    currentUser, activeChatId, setActiveChatId,
    sendMessage, hireMercenary, getMyChats, lang,
    startMission, completeMission,
  } = useStore();
  const [msgText, setMsgText] = useState('');
  const [, forceUpdate] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [mobileShowList, setMobileShowList] = useState(true);

  const rerender = () => forceUpdate(n => n + 1);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeChatId, msgText]);

  const chats = getMyChats();
  const activeChat = activeChatId ? getChatById(activeChatId) : null;
  const activeMission = activeChat?.missionId
    ? db.getMissionById(activeChat.missionId) : null;

  const handleSend = () => {
    if (!msgText.trim() || !activeChatId) return;
    sendMessage(activeChatId, msgText);
    setMsgText('');
    rerender();
  };

  const handleHire = () => {
    if (!activeChatId) return;
    const err = hireMercenary(activeChatId);
    if (err) { toast.error(t(lang, err)); return; }
    toast.success(t(lang, 'toast_hired'));
    rerender();
  };

  const handleStart = () => {
    if (!activeMission) return;
    const err = startMission(activeMission.id);
    if (err) { toast.error(t(lang, err)); return; }
    toast.success(t(lang, 'toast_started'));
    rerender();
  };

  const handleComplete = () => {
    if (!activeMission) return;
    const err = completeMission(activeMission.id);
    if (err) { toast.error(t(lang, err)); return; }
    toast.success(t(lang, 'toast_done'));
    rerender();
  };

  const selectChat = (id: string) => {
    setActiveChatId(id);
    setMobileShowList(false);
    rerender();
  };

  if (!currentUser) {
    return (
      <div className="chat-empty-auth">
        <span className="icon">💬</span>
        <p>Sign in to view your messages</p>
      </div>
    );
  }

  return (
    <div className="chat-layout">
      {/* Chat list */}
      <div className={`chat-list ${!mobileShowList ? 'mobile-hidden' : ''}`}>
        <div className="chat-list-header">
          📨 {t(lang, 'chat_title')}
        </div>
        {chats.length === 0 ? (
          <div className="empty-state" style={{ padding: '1rem', fontSize: '.8rem' }}>
            {t(lang, 'empty_chats')}
          </div>
        ) : (
          chats.map((chat) => {
            const otherId = chat.participantIds.find(id => id !== currentUser.id);
            const other = otherId ? getUserById(otherId) : undefined;
            const last = chat.messages[chat.messages.length - 1];
            const unread = chat.messages.filter(m => !m.read && m.senderId !== currentUser.id && m.senderId !== 'system').length;

            return (
              <div
                key={chat.id}
                className={`chat-item ${activeChatId === chat.id ? 'active' : ''}`}
                onClick={() => selectChat(chat.id)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') selectChat(chat.id); }}
              >
                {other && <Avatar user={other} size={36} />}
                <div className="chat-item-info">
                  <span className="chat-item-name">
                    {other ? `${sanitize(other.firstName)} ${sanitize(other.lastName)}` : 'Unknown'}
                  </span>
                  <span className="chat-item-preview">
                    {last ? (last.senderId === 'system' ? `⚡ ${last.text.slice(0, 35)}…` : last.text.slice(0, 35)) : 'New conversation'}
                  </span>
                </div>
                {unread > 0 && <span className="chat-unread">{unread}</span>}
              </div>
            );
          })
        )}
      </div>

      {/* Chat window */}
      <div className={`chat-window ${mobileShowList ? 'mobile-hidden' : ''}`}>
        {!activeChat ? (
          <div className="chat-placeholder">
            <span className="icon">💬</span>
            <p>{t(lang, 'chat_select')}</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="chat-header">
              <button className="chat-back-btn" onClick={() => setMobileShowList(true)} aria-label="Back to list">←</button>
              <div className="chat-header-info">
                {(() => {
                  const otherId = activeChat.participantIds.find(id => id !== currentUser.id);
                  const other = otherId ? getUserById(otherId) : undefined;
                  return (
                    <>
                      <span className="chat-header-name">
                        {other ? `${sanitize(other.firstName)} ${sanitize(other.lastName)}` : 'Chat'}
                      </span>
                      {activeMission && (
                        <span className="chat-mission-label">
                          📋 {sanitize(activeMission.title)} · €{activeMission.reward}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="chat-header-actions">
                {/* HIRE button */}
                {activeMission &&
                  activeMission.posterId === currentUser.id &&
                  activeMission.status === 'open' &&
                  activeChat.hireStatus === 'pending' && (
                  <button className="btn-hire" onClick={handleHire}>
                    🤠 {t(lang, 'mission_hire')}
                  </button>
                )}
                {/* START button */}
                {activeMission &&
                  activeMission.posterId === currentUser.id &&
                  activeMission.status === 'active' && (
                  <button className="btn btn-outline btn-sm" onClick={handleStart}>
                    ▶ {t(lang, 'mission_start')}
                  </button>
                )}
                {/* COMPLETE button */}
                {activeMission &&
                  activeMission.posterId === currentUser.id &&
                  activeMission.status === 'started' && (
                  <button className="btn btn-success btn-sm" onClick={handleComplete}>
                    ✓ {t(lang, 'mission_done')}
                  </button>
                )}
              </div>
            </div>

            {/* Payment tracker */}
            {activeMission && activeMission.status !== 'open' && (
              <div className="chat-payment-strip">
                <PaymentTracker mission={activeMission} />
              </div>
            )}

            {/* Messages */}
            <div className="chat-messages">
              {activeChat.messages.map((msg) => {
                if (msg.senderId === 'system') {
                  return <div key={msg.id} className="chat-system-msg">{sanitize(msg.text)}</div>;
                }
                const isMe = msg.senderId === currentUser.id;
                const time = new Date(msg.ts).toLocaleDateString([], { day: '2-digit', month: '2-digit' }) + ' - ' + new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                return (
                  <div key={msg.id} className={`msg ${isMe ? 'msg-me' : 'msg-them'}`}>
                    <span className="msg-text">{sanitize(msg.text)}</span>
                    <span className="msg-time">{time}</span>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="chat-input-area">
              <textarea
                className="chat-input"
                value={msgText}
                onChange={e => setMsgText(e.target.value)}
                placeholder={t(lang, 'chat_placeholder')}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                rows={2}
                maxLength={2000}
              />
              <button className="btn btn-gold btn-sm" onClick={handleSend}>{t(lang, 'chat_send')}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
